import {Banner, DotProgress} from '@wandb/components';
import React, {useRef, useState} from 'react';

import {Analytics} from '../../../services/analytics';
import {useBoundingClientRect} from '../../../util/hooks';
import {Code} from '../Code';
import type {FileBrowserFile} from '../types';
import {PatchItem} from './PatchItem';
import {PatchSummary} from './PatchSummary';
import type {RenderMode} from './types';
import {useParsePatch} from './useParsePatch';
import {usePatchStats} from './usePatchStats';
import {usePatchViewerThemeRegistration} from './usePatchViewerTheme';

const SMALL_SCREEN_THRESHOLD = 800;

type PatchViewerProps = {
  artifactId: string;
  file: FileBrowserFile;
  fileBody: string;
};

export function PatchViewer({artifactId, file, fileBody}: PatchViewerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const size = useBoundingClientRect(ref);
  const registered = usePatchViewerThemeRegistration();
  const [patch, error] = useParsePatch(fileBody);
  const patchStats = usePatchStats(patch);
  const [renderMode, setRenderMode] = useState<RenderMode>('side-by-side');
  const isSmallScreen = Boolean(size && size.width < SMALL_SCREEN_THRESHOLD);
  const boundedRenderMode = isSmallScreen ? 'inline' : renderMode;

  function handleSetRenderMode(renderMode: RenderMode) {
    Analytics.track('Patch file', {
      action: 'render mode cahnged',
      value: renderMode,
    });
    setRenderMode(renderMode);
  }

  if (error) {
    return (
      <>
        <Banner variant="warning" isDismissible={false}>
          There was an error in parsing patch file, displaying the raw diff
          instead.
        </Banner>
        <Code language="patch" text={fileBody} file={file} />
      </>
    );
  }

  if (!patch || !patchStats || !registered) {
    return <DotProgress aria-label="Loading file..." size="lg" />;
  }
  return (
    <div ref={ref}>
      <PatchSummary
        isSmallScreen={isSmallScreen}
        onSetRenderMode={handleSetRenderMode}
        renderMode={boundedRenderMode}
        stats={patchStats}
      />

      <ul className="flex flex-col gap-8">
        {patch.map((filePatch, index) => (
          // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
          <li key={index}>
            <PatchItem
              artifactId={artifactId}
              patch={filePatch}
              renderMode={boundedRenderMode}
              stats={patchStats.perPatch[index]}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
